import { useState, useEffect } from "react";
import Modal from "../../UI/Modal";
import Select from "../../UI/Select";
import Button from "../../UI/Button";
import { getGroupReportsWithSummaries } from "../../../services/reportService";
import { GroupReportWithSummaries } from "../../../models/pageModels/ReportDashboard";
import toast from "react-hot-toast";

interface AddSummaryModalProps {
    visible: boolean;
    onClose: () => void;
    groupId: string;
    onAdd: (reportId: string, fieldName: string, displayName: string) => Promise<void>;
    preselectedReportId?: string;
}

const AddSummaryModal = ({ 
    visible, 
    onClose, 
    groupId, 
    onAdd,
    preselectedReportId 
}: AddSummaryModalProps) => {
    const [step, setStep] = useState(1);
    const [reports, setReports] = useState<GroupReportWithSummaries[]>([]);
    const [selectedReportId, setSelectedReportId] = useState("");
    const [selectedFieldName, setSelectedFieldName] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const init = async () => {
            if (visible) {
                setIsSubmitting(false);
                const fetchedReports = await fetchReports();
                if (preselectedReportId && fetchedReports) {
                    setSelectedReportId(preselectedReportId);
                    setStep(2);
                } else {
                    setStep(1);
                    setSelectedReportId("");
                }
                setSelectedFieldName("");
            }
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, groupId, preselectedReportId]);

    const fetchReports = async () => {
        setIsLoading(true);
        try {
            const data = await getGroupReportsWithSummaries(groupId);
            setReports(data);
            return data;
        } catch (e) {
            toast.error("Error fetching reports");
        } finally {
            setIsLoading(false);
        }
    };

    const handleAdd = async () => {
        const report = reports.find(r => r.reportId === selectedReportId);
        const summary = report?.summaries.find(s => s.fieldName === selectedFieldName);
        
        if (report && summary) {
            setIsSubmitting(true);
            try {
                const displayName = `${report.reportName}: ${summary.fieldLabel}`;
                await onAdd(report.reportId, summary.fieldName, displayName);
                onClose();
            } catch (e) {
                // Error handled by parent/context
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const selectedReport = reports.find(r => r.reportId === selectedReportId);
    // Already-pinned summaries are excluded by the backend, no client-side filtering needed
    const availableSummaries = selectedReport?.summaries ?? [];

    return (
        <Modal 
            id="add-summary-modal" 
            title="Add Summary to Dashboard"
            className="small padding-horizontal-5"
            visible={visible} 
            onClose={onClose} 
        >
            {visible && (
                <div className="row">
                    <div className="column">
                        {step === 1 ? (
                            <>
                                <p className="margin-bottom-4">Select a report from the group:</p>
                                <Select
                                    id="report-select"
                                    value={selectedReportId}
                                    onChange={(val: string) => setSelectedReportId(val)}
                                    label="Report"
                                    disabled={isLoading}
                                >
                                    <option value="">-- Select a report --</option>
                                    {reports.map(r => (
                                        <option key={r.reportId} value={r.reportId}>{r.reportName}</option>
                                    ))}
                                </Select>

                                <div className="row justify-content-between margin-top-4">
                                    <div className="column-auto">
                                        <Button 
                                            id="cancel-add-step-1" 
                                            text="Cancel" 
                                            className="secondary" 
                                            onClick={onClose} 
                                        />
                                    </div>
                                    <div className="column-auto">
                                        <Button
                                            id="next-step"
                                            text="Next"
                                            className="primary"
                                            disabled={!selectedReportId || isLoading}
                                            onClick={() => setStep(2)}
                                        />
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <p className="margin-bottom-4">
                                    Select a summary from <strong>{selectedReport?.reportName}</strong>:
                                </p>
                                
                                {availableSummaries.length > 0 ? (
                                    <Select
                                        id="summary-select"
                                        value={selectedFieldName}
                                        onChange={(val: string) => setSelectedFieldName(val)}
                                        label="Summary"
                                        disabled={isSubmitting}
                                    >
                                        <option value="">-- Select a summary --</option>
                                        {availableSummaries.map(s => (
                                            <option key={s.fieldName} value={s.fieldName}>{s.fieldLabel}</option>
                                        ))}
                                    </Select>
                                ) : (
                                    <div className="alert alert-info">
                                        No available summaries found for this report.
                                    </div>
                                )}

                                <div className="row justify-content-between margin-top-4">
                                    <div className="column-auto">
                                        <Button 
                                            id="back-step" 
                                            text="Back" 
                                            className="secondary" 
                                            onClick={() => preselectedReportId ? onClose() : setStep(1)} 
                                        />
                                    </div>
                                    <div className="column-auto">
                                        <Button
                                            id="add-summary-confirm"
                                            text="Add to Dashboard"
                                            className="primary"
                                            loading={isSubmitting}
                                            disabled={!selectedFieldName || isSubmitting}
                                            onClick={handleAdd}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default AddSummaryModal;